import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { BOOKING_URL } from "@/lib/constants";

const SERVICES = [
  {
    tag: "Service 01",
    title: "Contingency Recovery",
    price: "20% of recovery",
    priceNote: "Only paid on successful recovery.",
    features: [
      "Zero upfront cost",
      "Full-service CAPE filing",
      "Entry-level audit",
      "CBP follow-up management",
    ],
    testId: "service-card-contingency",
    highlight: true,
  },
  {
    tag: "Service 02",
    title: "Advance Funding",
    price: "Cash now",
    priceNote: "Bundled with recovery.",
    features: [
      "Cash advance against your claim",
      "Fast decision timeline",
      "No personal guarantee",
      "Ideal for working capital",
      "Bundles with contingency",
    ],
    testId: "service-card-advance",
  },
  {
    tag: "Service 03",
    title: "Referral Partner",
    price: "Earn per recovery",
    priceNote: "Milestone-transparent fees.",
    features: [
      "Earn fees on every recovery",
      "Zero work post-introduction",
      "Client relationship protected",
      "White-glove service standard",
      "Transparent milestone reporting",
    ],
    testId: "service-card-referral",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32 bg-[#F9FAFB]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16"
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#0A1936]" />
              <span className="font-mono uppercase tracking-[0.24em] text-[11px] text-[#0A1936]/70">
                03 · Our Platform
              </span>
            </div>
            <h2 className="font-serif text-[#0A1936] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Three services.
              <br />
              <span className="italic">One seamless platform.</span>
            </h2>
          </div>
          <p className="text-lg text-[#0A1936]/70 max-w-md leading-relaxed">
            Choose the path that fits your business — combine recovery with
            funding, or introduce clients and earn on every recovery.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.title}
              data-testid={s.testId}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className={`relative overflow-hidden rounded-md p-8 lg:p-10 flex flex-col ${
                s.highlight
                  ? "bg-[#0A1936] text-white border border-[#F5B800]"
                  : "bg-white text-[#0A1936] border border-[#0A1936]/10"
              }`}
            >
              {s.highlight && (
                <div className="absolute top-6 right-6 font-mono uppercase text-[10px] tracking-[0.2em] bg-[#F5B800] text-[#0A1936] px-2 py-1 rounded-sm">
                  Most Popular
                </div>
              )}
              <div
                className={`font-mono uppercase tracking-[0.2em] text-[10px] ${
                  s.highlight ? "text-[#F5B800]" : "text-[#0A1936]/50"
                }`}
              >
                {s.tag}
              </div>
              <h3 className="font-serif text-3xl sm:text-4xl mt-4 leading-tight">
                {s.title}
              </h3>

              <div className="mt-8 pb-8 border-b border-current/10">
                <div
                  className={`font-serif italic text-4xl ${
                    s.highlight ? "text-[#F5B800]" : "text-[#0A1936]"
                  }`}
                >
                  {s.price}
                </div>
                <div
                  className={`mt-2 text-sm ${
                    s.highlight ? "text-white/60" : "text-[#0A1936]/60"
                  }`}
                >
                  {s.priceNote}
                </div>
              </div>

              <ul className="mt-8 space-y-3 flex-1">
                {s.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <Check
                      size={16}
                      className={`shrink-0 mt-0.5 ${
                        s.highlight ? "text-[#F5B800]" : "text-[#0A1936]"
                      }`}
                    />
                    <span
                      className={
                        s.highlight ? "text-white/90" : "text-[#0A1936]/80"
                      }
                    >
                      {f}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${s.testId}-cta`}
                className={`btn-magnetic mt-10 inline-flex items-center justify-between gap-3 rounded-sm px-6 py-4 font-semibold text-sm transition-colors ${
                  s.highlight
                    ? "bg-[#F5B800] hover:bg-[#DDA600] text-[#0A1936]"
                    : "bg-[#0A1936] hover:bg-[#1B2F55] text-white"
                }`}
              >
                <span>Book Assessment</span>
                <ArrowUpRight size={18} />
              </a>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
